/**
 * Market Segmentation View Store — per-project UI state for the Segments tab.
 *
 * Owns:
 *   - view ('cards' | 'list') — the layout for the Segments tab
 *
 * Persistence: localStorage via `persist` middleware, resets when the active
 * project changes (so a preference set on project A doesn't leak to project B).
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type SegmentsView = 'cards' | 'list'

interface State {
  projectId: string | null
  view: SegmentsView
  setProject: (projectId: string) => void
  setView: (view: SegmentsView) => void
}

const DEFAULT_VIEW: SegmentsView = 'cards'

export const useMarketSegmentationViewStore = create<State>()(
  persist(
    (set) => ({
      projectId: null,
      view: DEFAULT_VIEW,

      setProject: (projectId) =>
        set((s) => (s.projectId === projectId ? s : { projectId, view: DEFAULT_VIEW })),

      setView: (view) => set({ view }),
    }),
    {
      name: 'market-segmentation-view',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (s) => ({ projectId: s.projectId, view: s.view }),
    },
  ),
)

export const useSegmentsView = () => useMarketSegmentationViewStore((s) => s.view)
